// v409.3 — smart product-name matching for fast manual inventory entry.
// Understands shorthand like "reta 10" / "rt10" -> "Retatrutide 10mg",
// "bb20" -> "Wolverine (BB20)", "cjc" -> "CJC-1295 DAC 10mg", etc.

const norm = (s) => (s || "").toLowerCase().replace(/[-_/+]/g, " ").replace(/\s+/g, " ").trim();

// Split a token like "rt10" into ["rt", "10"]; keep pure tokens as-is.
const splitAlphaNum = (tok) => {
  const m = tok.match(/^([a-z]+)(\d+(?:\.\d+)?)$/);
  return m ? [m[1], m[2]] : [tok];
};

// "rt" matches "retatrutide": same first letter + remaining chars appear in order.
const abbrevMatch = (abbr, word) => {
  if (abbr.length < 2 || word[0] !== abbr[0]) return false;
  let i = 0;
  for (const ch of word) {
    if (ch === abbr[i]) i += 1;
    if (i === abbr.length) return true;
  }
  return false;
};

export const buildProductIndex = (names) =>
  names.map((name) => {
    const n = norm(name);
    const words = n.replace(/[()]/g, " ").split(" ").filter(Boolean);
    const alphaWords = words.map((w) => w.replace(/\d+(?:\.\d+)?/g, "")).filter((w) => w.length >= 2);
    const numbers = (n.match(/\d+(?:\.\d+)?/g) || []).map(Number);
    // Roman-numeral variants count as numbers: "Melanotan I" matches "mt1".
    const roman = { i: 1, ii: 2, iii: 3 };
    words.forEach((w) => { if (roman[w] != null) numbers.push(roman[w]); });
    return { name, n, words, alphaWords, numbers };
  });

export const matchProducts = (query, index, limit = 8) => {
  const q = norm(query);
  if (!q) return [];
  const rawTokens = q.split(" ").filter(Boolean);
  const alphas = [];
  const nums = [];
  rawTokens.forEach((tok) => {
    splitAlphaNum(tok).forEach((part) => {
      if (/^\d+(?:\.\d+)?$/.test(part)) nums.push(Number(part));
      else if (part) alphas.push(part);
    });
  });

  const scored = [];
  for (const p of index) {
    let score = 0;
    // Whole-query substring keeps the old exact behaviour on top.
    if (p.n.includes(q)) score += 6;

    // Every alpha token must land somewhere: word prefix > abbreviation.
    let alphasOk = true;
    for (const a of alphas) {
      if (p.words.some((w) => w.startsWith(a))) score += 3;
      else if (p.alphaWords.some((w) => abbrevMatch(a, w))) score += 2;
      else { alphasOk = false; break; }
    }
    if (!alphasOk && !p.n.includes(q)) continue;

    // Numbers must match a strength in the name (10 -> 10mg / BB20 -> 20).
    let numsOk = true;
    for (const num of nums) {
      if (p.numbers.includes(num)) score += 2;
      else { numsOk = false; break; }
    }
    if (!numsOk) continue;
    // No number given: slightly prefer products without competing strengths.
    if (score > 0) scored.push({ name: p.name, score });
  }
  scored.sort((a, b) => b.score - a.score || a.name.localeCompare(b.name));
  return scored.slice(0, limit).map((s) => s.name);
};
